import React, { useCallback, useRef, useState } from 'react';
import { Dialog } from '../lib/dialog';
import { Button } from '../lib/button';
import { ExclusiveButtons } from '../lib/exclusive-buttons';
import { FormField } from '../lib/forms';
import { Icon } from '../lib/icon';
import { Table, Column } from '../lib/table';
import { importFile, ImportErrorDetail, ImportResponse, ImportType } from '../service/importExportService';
import type { Field } from '../api/service';
import { getErrorMessage } from '../utils/errorUtils';
import { isHseLocationFile, LOCATION_IMPORT_DOMAIN_ID, resolveImportDomainId } from '../utils/locationImportUtils';

// ── CSV builder (used to reconstruct file after stripping auto-generated cols)
function buildCSV(columns: string[], rows: Record<string, string>[]): string {
  const escape = (val: string) =>
    val.includes(',') || val.includes('"') || val.includes('\n')
      ? `"${val.replace(/"/g, '""')}"`
      : val;
  return [
    columns.join(','),
    ...rows.map((row) => columns.map((col) => escape(row[col] ?? '')).join(',')),
  ].join('\r\n');
}

// ── Simple CSV parser (handles RFC 4180 quoting and UTF-8 BOM) ─────────────
function parseCSV(text: string): { columns: string[]; rows: Record<string, string>[] } {
  // Note: this parser does NOT support multi-line quoted fields (RFC 4180 §2.6).
  // A pre-scan regex cannot reliably detect them without re-implementing the full parser,
  // so we omit the early-exit check. Files with embedded newlines inside quoted fields
  // will parse incorrectly — the import will produce garbled rows and the backend
  // will reject or misprocess the data. Users should remove newlines from cell values.
  const clean = text.replace(/^\uFEFF/, '');
  const lines = clean.split(/\r?\n/).filter((l) => l.trim() !== '');
  if (lines.length === 0) return { columns: [], rows: [] };

  const parseRow = (line: string): string[] => {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        if (inQuotes && line[i + 1] === '"') { current += '"'; i++; }
        else inQuotes = !inQuotes;
      } else if (ch === ',' && !inQuotes) {
        result.push(current); current = '';
      } else {
        current += ch;
      }
    }
    result.push(current);
    return result;
  };

  const columns = parseRow(lines[0]);
  const rows = lines.slice(1).map((line, lineIndex) => {
    const vals = parseRow(line);
    if (vals.length !== columns.length) {
      console.warn(
        `CSV row ${lineIndex + 2} has ${vals.length} column(s) but header has ${columns.length}. ` +
        'Missing values will be treated as empty strings.',
      );
    }
    const row: Record<string, string> = {};
    columns.forEach((col, i) => { row[col] = vals[i] ?? ''; });
    return row;
  });

  return { columns, rows };
}

// ── Types ────────────────────────────────────────────────────────────────────
type Step = 'select' | 'preview';

interface PreviewData {
  columns: string[];
  rows: Record<string, string>[];
}

const IMPORT_OPTIONS: ImportType[] = ['create', 'update'];
const IMPORT_LABELS: Record<ImportType, string> = { create: 'Create', update: 'Update' };

export interface ImportDialogProps {
  open: boolean;
  onClose: () => void;
  /** Domain currently selected on MasterDataPage — sent to backend. */
  domainId?: string;
  /** All available fields for the domain (used to detect auto-generated columns). */
  fields?: Field[];
  onSuccess?: (result: ImportResponse) => void;
  onFailure?: (errors: ImportErrorDetail[]) => void;
}

export const ImportDialog: React.FC<ImportDialogProps> = ({
  open, onClose, domainId, fields, onSuccess, onFailure,
}) => {
  const [step, setStep] = useState<Step>('select');
  const [importType, setImportType] = useState<ImportType>('create');
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | undefined>();
  const [preview, setPreview] = useState<PreviewData | null>(null);
  const [loading, setLoading] = useState(false);
  const [networkError, setNetworkError] = useState<string | undefined>();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Keep a ref in sync so that async handlers always read the latest importType,
  // avoiding stale-closure bugs in event callbacks.
  const importTypeRef = useRef<ImportType>(importType);
  importTypeRef.current = importType;

  // Columns that the DB generates automatically — must be stripped for "Create" imports.
  const autoGeneratedCols = (fields ?? []).filter((f) => f.autoGenerated).map((f) => f.id);

  const resetState = () => {
    setStep('select');
    setFile(null);
    setFileError(undefined);
    setNetworkError(undefined);
    setImportType('create');
    setPreview(null);
  };

  const handleClose = () => {
    if (loading) return;
    resetState();
    onClose();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0] ?? null;
    setFile(selected);
    setFileError(undefined);
    setNetworkError(undefined);
    setPreview(null);
    e.target.value = '';
  };

  // ── Step 1 → 2: parse CSV and show preview ────────────────────────────
  const handlePreview = async () => {
    if (!file) { setFileError('Please select a file before previewing.'); return; }
    setFileError(undefined);
    try {
      const text = await file.text();
      const parsed = parseCSV(text);
      if (parsed.columns.length === 0) {
        setFileError('The file appears to be empty or has no columns.');
        return;
      }
      setPreview(parsed);
      setStep('preview');
    } catch (err: unknown) {
      // parseCSV throws Error for multi-line fields; file.text() throws DOMException for I/O issues.
      if (err instanceof Error && err.message.includes('multi-line')) {
        setFileError(err.message);
      } else if (err instanceof DOMException) {
        setFileError('Could not read the file. The file may have been moved or locked.');
      } else {
        setFileError('Could not parse the file. Make sure it is a valid UTF-8 CSV.');
      }
    }
  };

  // ── Step 2 → import ———————————————————————————————————————
  const handleImport = useCallback(async () => {
    if (!file || !preview) return;
    // Read from ref to guarantee latest importType even if the closure is stale.
    const currentImportType = importTypeRef.current;
    const effectiveDomainId = resolveImportDomainId(domainId, preview.columns);
    const isLocationImport = effectiveDomainId === LOCATION_IMPORT_DOMAIN_ID;
    setLoading(true);
    setNetworkError(undefined);
    try {
      // Always rebuild CSV from the parsed preview data (consistent UTF-8 encoding).
      // For "Create" imports: strip auto-generated columns so the backend does not reject
      // the file (e.g. customer_id is an IDENTITY column and cannot be specified on INSERT).
      // HSE location uploads keep all business columns and use domain_id=location.
      const colsToUpload = !isLocationImport && currentImportType === 'create'
        ? preview.columns.filter((c) => !autoGeneratedCols.includes(c))
        : preview.columns;
      const csvText = buildCSV(colsToUpload, preview.rows);
      const blob = new Blob([csvText], { type: 'text/csv' });
      const uploadFile = new File([blob], file.name, { type: 'text/csv' });
      const result = await importFile(currentImportType, uploadFile, effectiveDomainId);
      if (result.success) {
        resetState();
        onClose();
        onSuccess?.(result);
      } else {
        resetState();
        onClose();
        onFailure?.(result.errors);
      }
    } catch (err: unknown) {
      setNetworkError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file, preview, autoGeneratedCols, domainId, onClose, onSuccess, onFailure]);

  const previewUsesHseTemplate = preview ? isHseLocationFile(preview.columns) : false;

  // ── Preview table columns — hide auto-generated cols when type="create" (DB import only) ───
  const visiblePreviewCols = importType === 'create' && !previewUsesHseTemplate
    ? (preview?.columns ?? []).filter((c) => !autoGeneratedCols.includes(c))
    : (preview?.columns ?? []);

  const previewColumns: Column<Record<string, string>>[] = visiblePreviewCols.map((col) => ({
    key: col,
    title: col,
  }));

  const strippedNotice = importType === 'create' && !previewUsesHseTemplate && autoGeneratedCols.length > 0
    ? autoGeneratedCols.filter((c) => preview?.columns.includes(c))
    : [];

  // rowKey: use the row’s position in the data array — stable within a single preview session.
  // (Two rows can share the same first-column value, making a value-based key unreliable.)
  const previewRowKey = (_row: Record<string, string>, index: number): string => String(index);

  // ── Render ────────────────────────────────────────────────────────────
  if (step === 'preview' && preview) {
    return (
      <Dialog
        open={open}
        onClose={handleClose}
        title={`Import preview (${IMPORT_LABELS[importType]}) — ${preview.rows.length} row${preview.rows.length !== 1 ? 's' : ''}`}
        preventCloseOnOverlayClick={loading}
        footer={
          <Button variant="primary" onClick={handleImport} processing={loading}>
            {previewUsesHseTemplate ? 'Confirm validation' : 'Confirm import'}
          </Button>
        }
        footerExtras={
          <Button onClick={() => { if (!loading) setStep('select'); }} disabled={loading}>
            ← Back
          </Button>
        }
      >
        {networkError && (
          <p className="help is-danger" style={{ marginBottom: 8 }}>{networkError}</p>
        )}
        {previewUsesHseTemplate && (
          <p className="help" style={{ marginBottom: 8, color: '#0B419D' }}>
            HSE location template detected — rows will be validated against master data rules.
          </p>
        )}
        {strippedNotice.length > 0 && (
          <p className="help is-warning" style={{ marginBottom: 8, color: '#946c00' }}>
            ⚠️ Auto-generated database columns will be excluded from import: <strong>{strippedNotice.join(', ')}</strong>
          </p>
        )}
        <div style={{ overflowX: 'auto', maxHeight: 400, overflowY: 'auto' }}>
          <Table
            data={preview.rows}
            columns={previewColumns}
            rowKey={previewRowKey}
          />
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      title="Import data"
      footer={
        <Button variant="primary" onClick={handlePreview}>
          Preview →
        </Button>
      }
      footerExtras={<Button onClick={handleClose}>Cancel</Button>}
    >
      <FormField label="Import type" required>
        <ExclusiveButtons<ImportType>
          items={IMPORT_OPTIONS}
          value={importType}
          onChange={setImportType}
          textFn={(v) => IMPORT_LABELS[v]}
        />
      </FormField>

      <FormField label="File" required error={fileError}>
        <p className="help" style={{ marginBottom: 8 }}>
          Use a CSV file. For the HSE location template, save the Excel sheet as CSV (UTF-8).
        </p>
        <div className="field has-addons" style={{ marginBottom: 0 }}>
          <div className="control is-expanded">
            <input
              className={`input${fileError ? ' is-danger' : ''}`}
              type="text"
              readOnly
              placeholder="No file selected..."
              value={file?.name ?? ''}
              style={{ cursor: 'default' }}
              aria-label="Selected file"
            />
          </div>
          <div className="control">
            <Button
              type="button"
              title="Browse for file"
              aria-label="Browse for file"
              onClick={() => fileInputRef.current?.click()}
            >
              <Icon name="file" style={{ fontSize: '16px' }} />
            </Button>
          </div>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          style={{ display: 'none' }}
          onChange={handleFileChange}
          aria-hidden="true"
        />
      </FormField>

      {networkError && (
        <p className="help is-danger" style={{ marginTop: 8 }}>{networkError}</p>
      )}
    </Dialog>
  );
};